import React, { useContext, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useCurrency } from '@/hooks/useCurrency';
import { useIvaPricing } from '@/hooks/useIvaPricing';
import WeightInputHelp from '@/components/products/WeightInputHelp';
import type { Product } from '@/types';
import {
  calculateWeightLinePrice,
  formatWeight,
  toKg,
  type WeightInputUnit,
} from '@/utils/weightPricing';

interface WeightInputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  onConfirm: (weightKg: number) => void;
}

const WeightInputDialog: React.FC<WeightInputDialogProps> = ({
  open,
  onOpenChange,
  product,
  onConfirm,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const { formatAmount } = useCurrency();
  const { showIvaPrice, getPriceWithIva } = useIvaPricing();
  const [rawValue, setRawValue] = useState('');
  const [unit, setUnit] = useState<WeightInputUnit>('g');

  const kg = useMemo(() => toKg(rawValue, unit), [rawValue, unit]);
  const linePrice = useMemo(
    () => (product && kg != null ? calculateWeightLinePrice(product.price, kg) : 0),
    [product, kg]
  );
  const isValid = kg != null && kg > 0;

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{translator('ProductsCatalog.enterWeightTitle')}</DialogTitle>
          <DialogDescription>
            {product.name} — {formatAmount(product.price)}
            {translator('Products.pricePerKgBadge')}
            <br />
            {translator('ProductsCatalog.enterWeightDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="weight-input">{translator('ProductsCatalog.weightLabel')}</Label>
                <WeightInputHelp />
              </div>
              <Input
                id="weight-input"
                type="text"
                inputMode="decimal"
                value={rawValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
                    setRawValue(value);
                  }
                }}
                placeholder={unit === 'g' ? '500' : '0.5'}
                autoFocus
              />
            </div>
            <Select value={unit} onValueChange={(v) => setUnit(v as WeightInputUnit)}>
              <SelectTrigger className="w-[88px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="g">{translator('ProductsCatalog.weightUnitGrams')}</SelectItem>
                <SelectItem value="kg">{translator('ProductsCatalog.weightUnitKg')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rawValue.trim() && !isValid && (
            <p className="text-sm text-destructive">{translator('ProductsCatalog.weightInvalid')}</p>
          )}

          {isValid && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground">
                {translator('ProductsCatalog.weightPriceBreakdown', {
                  weight: formatWeight(kg!),
                  pricePerKg: formatAmount(product.price),
                })}
              </p>
              <p className="text-sm font-medium">
                {translator('ProductsCatalog.weightCalculatedPrice')}: {formatAmount(linePrice)}
              </p>
              {showIvaPrice && linePrice > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formatAmount(getPriceWithIva(linePrice))} {translator('Global.ivaIncluded')}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {translator('Global.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!isValid}
            onClick={() => {
              if (!isValid || kg == null) return;
              onConfirm(kg);
              onOpenChange(false);
            }}
          >
            {translator('ProductsCatalog.confirmWeight')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WeightInputDialog;
